import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/**
 * Shared stub for /legal/privacy and /legal/terms. Kept intentionally light —
 * final legal text goes in as a Markdown import when Legal delivers it.
 */
export default function Legal({ variant }) {
  const { t } = useTranslation("landing");
  const key = variant === "terms" ? "terms" : "privacy";

  useEffect(() => {
    document.title = `${t(`legal.${key}.title`)} · EvidenceLab`;
  }, [t, key]);

  return (
    <div
      data-testid={`legal-${key}-page`}
      className="min-h-screen bg-editorial-paper"
    >
      <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid={`legal-${key}-back`}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2} />
            {t("legal.back")}
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 pb-24 pt-16 sm:px-6 lg:px-8">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-700/90">
          {key === "terms" ? "Terms of service" : "Privacy notice"}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight tracking-tight text-slate-900 sm:text-5xl">
          {t(`legal.${key}.title`)}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
          {t(`legal.${key}.subtitle`)}
        </p>
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <p className="text-base leading-relaxed text-slate-700 sm:text-[17px]">
            {t(`legal.${key}.body`)}
          </p>
        </div>
      </article>
    </div>
  );
}
